/**
 * PurchaseOrders.tsx — PO list page  (Phase 65 → Phase 67 UI overhaul)
 *
 * Route: /purchase-orders
 * Accessible by: ADMIN, POWER_USER, PO_REQUESTOR, PO_APPROVER, PO_SENIOR_APPROVER
 *
 * Features:
 *  • Navy header bar matching InvoiceList design
 *  • Summary tiles (white cards, box-shadow)
 *  • Pill-shaped status tabs + rounded search in filter bar card
 *  • Inline CSSProperties (no Tailwind) — matches portal design system
 *  • Role-aware action buttons (Create, View, Edit, Issue)
 *  • Pagination + auto-refresh
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getWinTeamEnvironment, getBranding } from '../api';
import { listPOs } from '../api/poApi';
import type { POListParams } from '../api/poApi';
import {
  PO_STATUS_LABELS,
  PO_AP_ROLES,
  PO_REQUESTOR_ROLES,
  type POSummary,
  type POStatus,
} from '../types/poTypes';

const PAGE_SIZE = 25;

const STATUS_TABS: { label: string; value: POStatus | 'ALL' | 'PENDING' }[] = [
  { label: 'All',                value: 'ALL' },
  { label: 'Pending',           value: 'PENDING' },
  { label: 'Draft',             value: 'DRAFT' },
  { label: 'Approved',          value: 'APPROVED' },
  { label: 'Issued',            value: 'ISSUED' },
  { label: 'Partially Invoiced', value: 'PARTIALLY_INVOICED' },
  { label: 'Closed',            value: 'CLOSED' },
  { label: 'Declined',          value: 'DECLINED' },
];

/** Inline status badge colours — replaces Tailwind PO_STATUS_COLORS */
const STATUS_BADGE_STYLES: Record<POStatus, { bg: string; color: string }> = {
  DRAFT:                   { bg: '#f0f2f5', color: '#555' },
  PENDING_APPROVAL:        { bg: '#fff8e1', color: '#b8860b' },
  PENDING_SENIOR_APPROVAL: { bg: '#fff3e0', color: '#e65100' },
  APPROVED:                { bg: '#e3f2fd', color: '#1565c0' },
  ISSUED:                  { bg: '#e8f5e9', color: '#2d7a2d' },
  PARTIALLY_INVOICED:      { bg: '#e0f2f1', color: '#00695c' },
  CLOSED:                  { bg: '#f5f5f5', color: '#757575' },
  DECLINED:                { bg: '#fdecea', color: '#c0392b' },
};

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function ageLabel(dateStr: string | null): { label: string; color: string } {
  if (!dateStr) return { label: '—', color: '#999' };
  const hours = (Date.now() - new Date(dateStr).getTime()) / 36e5;
  if (hours < 24)  return { label: `${Math.floor(hours)}h ago`,        color: '#2d7a2d' };
  if (hours < 48)  return { label: `${Math.floor(hours)}h ago`,        color: '#b8860b' };
  return             { label: `${Math.floor(hours / 24)}d ago`, color: '#c0392b' };
}

interface SummaryTileData {
  label: string;
  value: number | string;
  color: string;
}

const PurchaseOrders: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout, isAdmin, isPowerUser } = useAuth();

  const [pos, setPos]               = useState<POSummary[]>([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState<string | null>(null);

  const [activeTab, setActiveTab]   = useState<string>('ALL');
  const [search, setSearch]         = useState('');
  const [searchCommit, setSearchCommit] = useState('');
  const [jobFilter, setJobFilter]   = useState('');

  const [tiles, setTiles]           = useState<SummaryTileData[]>([]);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);

  // Branding & environment
  const [reverseLogoUrl, setReverseLogoUrl] = useState('');
  const [wtEnvironment, setWtEnvironment] = useState<string | null>(null);
  const [wtDbName, setWtDbName]           = useState<string | null>(null);

  const canCreate = PO_REQUESTOR_ROLES.includes(user?.role ?? '');
  const canAP     = PO_AP_ROLES.includes(user?.role ?? '');

  // Load branding + environment once
  useEffect(() => {
    getBranding().then(b => {
      if (b.reverse_logo_url) setReverseLogoUrl(b.reverse_logo_url);
      if (b.portal_tab) document.title = b.portal_tab;
    });
    getWinTeamEnvironment()
      .then(r => { setWtEnvironment(r.environment); setWtDbName(r.db_name); })
      .catch(() => {});
  }, []);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setSearchCommit(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchList = useCallback(async (overridePage = page, silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const params: POListParams = {
        page: overridePage,
        page_size: PAGE_SIZE,
        search: searchCommit || undefined,
        job_number: jobFilter || undefined,
      };

      if (activeTab === 'PENDING') {
        // Backend doesn't have a PENDING pseudo-status
      } else if (activeTab !== 'ALL') {
        params.status = activeTab as POStatus;
      }

      const res = await listPOs(params);
      setPos(res.pos);
      setTotal(res.total);
      setLastRefreshed(new Date());

      // Build tiles on unfiltered first page
      if (overridePage === 1 && !searchCommit && !jobFilter && activeTab === 'ALL') {
        buildTiles(res.pos, res.total);
      }
    } catch (err: any) {
      if (!silent) setError(err?.response?.data?.error || 'Failed to load purchase orders');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [page, searchCommit, jobFilter, activeTab]);

  useEffect(() => {
    fetchList(1);
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, jobFilter, searchCommit]);

  useEffect(() => {
    fetchList(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => fetchList(page, true), 30_000);
    return () => clearInterval(interval);
  }, [fetchList, page]);

  function buildTiles(rows: POSummary[], tot: number) {
    const pending = rows.filter(
      p => p.status === 'PENDING_APPROVAL' || p.status === 'PENDING_SENIOR_APPROVAL',
    ).length;
    const issued   = rows.filter(p => p.status === 'ISSUED' || p.status === 'PARTIALLY_INVOICED').length;
    const totalAmount = rows.reduce((sum, p) => sum + p.total_amount, 0);
    setTiles([
      { label: 'Total POs',         value: tot,                                                          color: '#00386C' },
      { label: 'Total Value',       value: `$${totalAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, color: '#00386C' },
      { label: 'Pending Approval',  value: pending,  color: pending > 0 ? '#b8860b' : '#2d7a2d' },
      { label: 'Issued / Active',   value: issued,   color: '#2d7a2d' },
    ]);
  }

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f0f2f5', fontFamily: 'Segoe UI, sans-serif' }}>

      {/* ── Navy Header ── */}
      <div style={{ backgroundColor: '#00386C', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '56px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {reverseLogoUrl
            ? <img src={reverseLogoUrl} alt="Logo" style={{ height: '28px', objectFit: 'contain', display: 'block' }} />
            : <span style={{ color: 'white', fontWeight: 700, fontSize: '16px' }}>AP Portal</span>
          }
          {isAdmin && (
            <span style={{ backgroundColor: '#b8860b', color: 'white', fontSize: '11px', fontWeight: 700, padding: '2px 8px', borderRadius: '10px' }}>
              ADMIN
            </span>
          )}
          {wtEnvironment && (
            <span style={{
              backgroundColor: wtEnvironment === 'PRODUCTION' ? '#c0392b' : '#1a7a4a',
              color: 'white', fontSize: '11px', fontWeight: 700,
              padding: '2px 8px', borderRadius: '10px', letterSpacing: '0.5px',
            }}>
              {wtEnvironment === 'PRODUCTION' ? '⚠ PRODUCTION' : 'TRAINING'}{wtDbName ? ` — ${wtDbName}` : ''}
            </span>
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <button
            onClick={() => navigate('/invoices')}
            style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: '4px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
          >
            Invoices
          </button>
          {isPowerUser && (
            <button onClick={() => navigate('/credit-card-upload')} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: '4px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
              Credit Card Upload
            </button>
          )}
          {isAdmin && (
            <button onClick={() => navigate('/admin')} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: '4px 14px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
              Admin Panel
            </button>
          )}
          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '13px' }}>{user?.display_name}</span>
          <button onClick={logout} style={{ background: 'none', border: '1px solid rgba(255,255,255,0.4)', color: 'white', padding: '4px 12px', borderRadius: '4px', cursor: 'pointer', fontSize: '13px' }}>
            Sign out
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>

        {/* ── Page title + Create button ── */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' }}>
          <div>
            <h1 style={{ margin: 0, fontSize: '22px', fontWeight: 700, color: '#1a202c' }}>Purchase Orders</h1>
            <p style={{ margin: '4px 0 0', fontSize: '13px', color: '#718096' }}>
              Create, track, and manage POs for vendor purchases.
            </p>
          </div>
          {canCreate && (
            <button
              onClick={() => navigate('/purchase-orders/new')}
              style={{
                padding: '9px 20px', backgroundColor: '#00386C', color: 'white',
                border: 'none', borderRadius: '6px', cursor: 'pointer',
                fontSize: '14px', fontWeight: 600,
              }}
            >
              + New Purchase Order
            </button>
          )}
        </div>

        {/* ── Summary tiles ── */}
        {tiles.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '24px' }}>
            {tiles.map(tile => (
              <div key={tile.label} style={{ backgroundColor: 'white', borderRadius: '8px', padding: '20px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>
                <p style={{ margin: '0 0 8px', fontSize: '12px', color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{tile.label}</p>
                <p style={{ margin: 0, fontSize: '28px', fontWeight: 700, color: tile.color }}>
                  {typeof tile.value === 'number' ? tile.value.toLocaleString() : tile.value}
                </p>
              </div>
            ))}
          </div>
        )}

        {/* ── Filter bar ── */}
        <div style={{
          backgroundColor: 'white', borderRadius: '8px', padding: '16px 20px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)', marginBottom: '16px',
          display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
        }}>
          {/* Status tabs (pill-shaped) */}
          {STATUS_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setActiveTab(tab.value)}
              style={{
                padding: '6px 14px', borderRadius: '20px', border: 'none',
                cursor: 'pointer', fontSize: '13px', fontWeight: 600,
                backgroundColor: activeTab === tab.value ? '#00386C' : '#f0f2f5',
                color: activeTab === tab.value ? 'white' : '#555',
              }}
            >
              {tab.label}
            </button>
          ))}

          {/* Search input */}
          <input
            placeholder="Search PO #, vendor, description…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              marginLeft: 'auto', padding: '6px 12px',
              border: '1px solid #ddd', borderRadius: '20px',
              fontSize: '13px', width: '240px', outline: 'none',
            }}
          />

          {/* Job filter */}
          <input
            placeholder="Job # filter"
            value={jobFilter}
            onChange={e => { setJobFilter(e.target.value); setPage(1); }}
            style={{
              padding: '6px 12px',
              border: '1px solid #ddd', borderRadius: '20px',
              fontSize: '13px', width: '130px', outline: 'none',
            }}
          />

          {/* Live refresh indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginLeft: '8px' }}>
            <span style={{
              display: 'inline-block', width: '7px', height: '7px', borderRadius: '50%',
              backgroundColor: '#2d7a2d',
              boxShadow: '0 0 0 2px rgba(45,122,45,0.25)',
            }} />
            <span style={{ fontSize: '11px', color: '#999', whiteSpace: 'nowrap' }}>
              {lastRefreshed
                ? `Updated ${lastRefreshed.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })}`
                : 'Live'}
            </span>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div style={{
            backgroundColor: '#fdecea', border: '1px solid #f5c6cb', borderRadius: '8px',
            padding: '12px 20px', marginBottom: '16px', fontSize: '13px', color: '#c0392b',
          }}>
            {error}
          </div>
        )}

        {/* ── PO Table ── */}
        <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#999' }}>Loading purchase orders…</div>
          ) : pos.length === 0 ? (
            <div style={{ padding: '48px', textAlign: 'center', color: '#999' }}>No purchase orders found.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e9ecef' }}>
                  {['PO #', 'Vendor', 'Job', 'Total', 'Invoiced', 'Status', 'Requested', 'Age', ''].map(h => (
                    <th key={h} style={{
                      padding: '12px 16px',
                      textAlign: h === 'Total' || h === 'Invoiced' ? 'right' : 'left',
                      fontWeight: 600, color: '#555', whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pos.map((po, i) => {
                  const sc = STATUS_BADGE_STYLES[po.status] || { bg: '#f0f2f5', color: '#555' };
                  const age = ageLabel(po.requested_at);
                  return (
                    <tr
                      key={po.id}
                      onClick={() => navigate(`/purchase-orders/${po.id}`)}
                      style={{
                        backgroundColor: i % 2 === 0 ? 'white' : '#fafafa',
                        borderBottom: '1px solid #e9ecef',
                        cursor: 'pointer',
                      }}
                      onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f0f4ff')}
                      onMouseLeave={e => (e.currentTarget.style.backgroundColor = i % 2 === 0 ? 'white' : '#fafafa')}
                    >
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: '#00386C', fontFamily: 'monospace' }}>
                        {po.po_number}
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 600, color: '#1a1a1a' }}>{po.vendor_name}</td>
                      <td style={{ padding: '12px 16px', color: '#444' }}>
                        {po.job_number}
                        {po.job_name && (
                          <span style={{ color: '#999', marginLeft: '4px' }}>— {po.job_name}</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', fontWeight: 600 }}>
                        {fmtCurrency(po.total_amount)}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'right', color: '#666' }}>
                        {po.invoiced_amount > 0 ? fmtCurrency(po.invoiced_amount) : '—'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{
                          padding: '3px 10px', borderRadius: '12px',
                          fontSize: '11px', fontWeight: 700,
                          backgroundColor: sc.bg, color: sc.color,
                        }}>
                          {PO_STATUS_LABELS[po.status]}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', color: '#666' }}>{fmtDate(po.requested_at)}</td>
                      <td style={{ padding: '12px 16px', color: age.color, fontWeight: 600, fontSize: '12px' }}>{age.label}</td>
                      <td style={{ padding: '12px 16px' }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span
                            onClick={() => navigate(`/purchase-orders/${po.id}`)}
                            style={{ color: '#00386C', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                          >
                            View →
                          </span>
                          {po.status === 'DRAFT' && canCreate && (
                            <span
                              onClick={() => navigate(`/purchase-orders/${po.id}/edit`)}
                              style={{ color: '#555', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                            >
                              Edit
                            </span>
                          )}
                          {po.status === 'APPROVED' && canAP && (
                            <span
                              onClick={() => navigate(`/purchase-orders/${po.id}`)}
                              style={{ color: '#2d7a2d', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                            >
                              Issue
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '12px 20px', borderTop: '1px solid #e9ecef', backgroundColor: '#f8f9fa',
            }}>
              <span style={{ fontSize: '12px', color: '#718096' }}>
                Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
              </span>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{
                    padding: '5px 14px', borderRadius: '4px', fontSize: '12px', fontWeight: 600,
                    border: '1px solid #cbd5e0', backgroundColor: 'white', color: '#4a5568',
                    cursor: page === 1 ? 'default' : 'pointer',
                    opacity: page === 1 ? 0.4 : 1,
                  }}
                >
                  ← Prev
                </button>
                <span style={{ fontSize: '12px', color: '#718096' }}>
                  Page {page} / {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  style={{
                    padding: '5px 14px', borderRadius: '4px', fontSize: '12px', fontWeight: 600,
                    border: '1px solid #cbd5e0', backgroundColor: 'white', color: '#4a5568',
                    cursor: page === totalPages ? 'default' : 'pointer',
                    opacity: page === totalPages ? 0.4 : 1,
                  }}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

export default PurchaseOrders;
